import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Copy, Download, Users } from "lucide-react";
import AdminModal from "@/components/AdminModal";

const buildMonths = (lang) => {
  const out = [{ value: "all", label: lang === "ar" ? "كل العمليات" : "All activity" }];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", { month: "long", year: "numeric" });
    out.push({ value, label });
  }
  return out;
};

export default function ProfileModal({ open, onClose, onSuccess }) {
  const { t, lang } = useApp();
  const { user, setUser } = useAuth();
  const [mobile, setMobile] = useState(user?.mobile || "");
  const [loading, setLoading] = useState(false);
  const [statementMonth, setStatementMonth] = useState("all");
  const [arEnabled, setArEnabled] = useState(false);
  const [arAmount, setArAmount] = useState("100");
  const [showAdmin, setShowAdmin] = useState(false);
  const months = React.useMemo(() => buildMonths(lang), [lang]);

  React.useEffect(() => {
    if (open) {
      setMobile(user?.mobile || "");
      api.get("/wallet/autoreload").then((r) => {
        setArEnabled(r.data.enabled);
        setArAmount(String(r.data.amount || 100));
      }).catch(() => {});
    }
  }, [open, user]);

  const saveAutoReload = async (enabled, amount) => {
    setArEnabled(enabled);
    try {
      await api.put("/wallet/autoreload", { enabled, amount: parseFloat(amount) });
      toast.success(t.toastAutoReloadSaved);
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    }
  };

  const save = async () => {
    setLoading(true);
    try {
      const res = await api.put("/profile", { mobile });
      setUser(res.data);
      toast.success(t.toastProfileSaved);
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  const copyHandle = () => {
    navigator.clipboard.writeText("@" + user?.handle);
    toast.success(t.toastHandleCopied);
  };

  const downloadStatement = async () => {
    try {
      const params = statementMonth !== "all" ? { month: statementMonth } : {};
      const res = await api.get("/statement", { responseType: "blob", params });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `sbpay-statement-${statementMonth}-${user?.handle || "wallet"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t.toastStatementReady);
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="profile-modal" className="max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle className="text-center">{t.profile}</DialogTitle>
          <DialogDescription className="sr-only">{t.profile}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center pt-1">
          <Avatar className="w-20 h-20 border-2 border-primary/30">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-primary/15 text-primary font-bold text-2xl">
              {user?.name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <p className="font-bold text-lg mt-3">{user?.name}</p>
          <button onClick={copyHandle} className="flex items-center gap-1.5 text-primary font-semibold text-sm mt-1">
            @{user?.handle} <Copy className="w-3.5 h-3.5" />
          </button>
          <p className="text-xs text-muted-foreground mt-1">{user?.email}</p>
          {user?.iban && (
            <button
              data-testid="profile-iban"
              onClick={() => { navigator.clipboard.writeText(user.iban); toast.success(t.copied); }}
              className="mt-2 flex items-center gap-1.5 text-[11px] font-mono text-muted-foreground hover:text-foreground"
            >
              <span className="text-muted-foreground">{t.myIban}:</span> {user.iban}
            </button>
          )}
        </div>
        <div className="mt-4">
          <label className="text-xs font-semibold text-muted-foreground">{t.mobileNumber}</label>
          <Input
            data-testid="profile-mobile-input"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            placeholder={t.mobilePlaceholder}
            className="h-12 rounded-2xl mt-1.5 tabular-nums"
          />
          <Button
            data-testid="profile-save"
            onClick={save}
            disabled={loading}
            className="w-full h-[52px] rounded-2xl text-base font-bold mt-4"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t.save}
          </Button>

          <div className="mt-4 p-4 rounded-2xl bg-muted/50 border border-border" data-testid="autoreload-section">
            <div className="flex items-center justify-between">
              <div className="min-w-0 pe-2">
                <p className="text-sm font-bold">{t.autoReload}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t.autoReloadDesc}</p>
              </div>
              <Switch
                data-testid="autoreload-toggle"
                checked={arEnabled}
                onCheckedChange={(v) => saveAutoReload(v, arAmount)}
              />
            </div>
            {arEnabled && (
              <div className="mt-3">
                <label className="text-xs font-semibold text-muted-foreground">{t.autoReloadAmount}</label>
                <Select
                  value={arAmount}
                  onValueChange={(v) => { setArAmount(v); saveAutoReload(true, v); }}
                >
                  <SelectTrigger data-testid="autoreload-amount-select" className="h-11 rounded-2xl mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["50", "100", "200", "500", "1000"].map((a) => (
                      <SelectItem key={a} value={a} data-testid={`autoreload-amount-${a}`}>
                        {Number(a).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")} {t.currency}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="mt-2.5">
            <Select value={statementMonth} onValueChange={setStatementMonth}>
              <SelectTrigger data-testid="statement-month-select" className="h-11 rounded-2xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {months.map((m) => (
                  <SelectItem key={m.value} value={m.value} data-testid={`statement-month-${m.value}`}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            data-testid="profile-download-statement"
            onClick={downloadStatement}
            variant="outline"
            className="w-full h-[48px] rounded-2xl text-sm font-bold mt-2.5 gap-2"
          >
            <Download className="w-4 h-4" /> {t.downloadStatement}
          </Button>

          {user?.is_admin && (
            <Button
              data-testid="profile-admin-btn"
              onClick={() => setShowAdmin(true)}
              className="w-full h-[48px] rounded-2xl text-sm font-bold mt-2.5 gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Users className="w-4 h-4" /> {t.viewCustomers}
            </Button>
          )}
        </div>
      </DialogContent>
      <AdminModal open={showAdmin} onClose={() => setShowAdmin(false)} />
    </Dialog>
  );
}
